import { createFeatureSelector, createSelector } from '@ngrx/store';
import { sortDataByConfig } from '../../../../features/energy-analytics/health/src/lib/kpis-distribution/kpis-distribution.config';
import { KpiDistributionInterface, SoHUnit } from '../../../../features/energy-analytics/health/src/lib/models/kpis-distribution.models';
import { HEALTH_FEATURE_KEY, State, StringsDistributionState, stringsDistributionAdapter } from '../reducers/health.reducer';

import { getStringInstalledEnergy } from '../selectors/systems.selectors'

const { selectAll } = stringsDistributionAdapter.getSelectors();

function getSohMultiplier(stringinstalledenergy: number | undefined, sohUnit: string){
  let multiplier;
  if (sohUnit === "kWh") multiplier = 1/1e3;
  else if (sohUnit === "%") multiplier = stringinstalledenergy !== undefined ? 100 / stringinstalledenergy : undefined;

  return multiplier || 1;
}

export const getHealthState = createFeatureSelector<State>(HEALTH_FEATURE_KEY);

export const getStringsDistributionState = createSelector(
  getHealthState,
  getStringInstalledEnergy,
  (state, stringinstalledenergy) => {
    const multiplier = getSohMultiplier(stringinstalledenergy, state.sohUnit);
    return {
      ...state.stringsDistribution,
      entities:  Object.fromEntries(
        Object.entries(state.stringsDistribution.entities).map(([key, value]) => [key, {...value, sohC: value?.sohC && value.sohC * multiplier}])
      )
    }
  });

export const getRawDistributionList = createSelector(getStringsDistributionState, selectAll);
export const getSelectedStringsDistributionIds = createSelector(getStringsDistributionState, (state) => state.selectedIds ?? []);

export const getSelectedStringsDistributionItems = createSelector(
  getStringsDistributionState,
  (state) => state?.selectedIds?.map((id) => ({ id, name: state.entities[id]?.containerName })) ?? []
);

export const getStringsDistribution = createSelector(getStringsDistributionState, (state: StringsDistributionState) => state);

export const getHealthKpiData = createSelector(
  getHealthState,
  getStringInstalledEnergy,
  (state, stringinstalledenergy) => {
    if (state.healthKpis.kpisChartData["sohC"]?.data === undefined) return state.healthKpis;

    const multiplier = getSohMultiplier(stringinstalledenergy, state.sohUnit);

    return {...state.healthKpis,
      kpisChartData: {
        ...state.healthKpis.kpisChartData,
        ["sohC"]: {
          ...state.healthKpis.kpisChartData["sohC"],
          data: {x: [state.healthKpis.kpisChartData["sohC"].data.x[0].map(x => x * multiplier)], y:[]}
        }
      },
      kpisChartDataInfo: {
        ... state.healthKpis.kpisChartDataInfo,
        ["sohC"]: state.healthKpis.kpisChartDataInfo["sohC"].map((x: any) => ({...x, valueZip: {...x.valueZip, value: x.valueZip.value * multiplier}}))
      }
    };
  });

export const getSoHDisplayUnit = createSelector(getHealthState, (state) => state.sohUnit);
export const getAvailableSoHDisplayUnits = createSelector(getStringInstalledEnergy, (stringinstalledenergy) => stringinstalledenergy === undefined ? [SoHUnit.KWh] : [SoHUnit.Percent, SoHUnit.KWh]);

export const getStringsDistributionList = createSelector(
  getStringsDistributionState,
  (state: any, props: { page: number; pageSize: number }) => {
    let data = selectAll(state);

    if (Object.keys(state.customFilters ?? {}).length) {
      data = filterDistributionList(data, state.customFilters);
    }

    if (state.config.searchString) {
      data = data.filter((item) => searchInAllKeys(item, state.config.searchString));
    }

    if (state.config.order) {
      const [key, direction] = state.config.order.split(':');
      data = sortDataByConfig(data, key as keyof KpiDistributionInterface, direction as 'asc' | 'desc');
    }

    const start = (props.page - 1) * props.pageSize;
    const end = start + props.pageSize;
    return { data: data.slice(start, end), totalCount: data.length };
  }
);

function searchInAllKeys(item: KpiDistributionInterface, searchString: string): boolean {
  if (!searchString) return false;

  const formattedSearchString = searchString.trim().toLowerCase();

  const valueStr = String(item.containerName).toLowerCase();
  return valueStr.includes(formattedSearchString);
}

function filterDistributionList(healthKpiList: KpiDistributionInterface[], kpiFilter: any): KpiDistributionInterface[] {
  const kpiName = Object.keys(kpiFilter)[0];
  const filters = kpiFilter[kpiName];

  return healthKpiList.reduce((result, val) => {
    for (const filter of filters) {
      if (
        filter &&
        ((filter.min === 0 && filter.max === 0 && val[kpiName] == null) ||
          (val[kpiName] >= filter.min && val[kpiName] < filter.max))
      ) {
        result.push(val);
        break;
      }
    }
    return result;
  }, []);
}
